import React, { useState } from "react";
import { Field, Form, Formik, useFormikContext } from "formik";
import * as Yup from "yup";
import moment from "moment";
import {
  FaBriefcaseMedical,
  FaCalendar,
  FaCheckDouble,
  FaUser,
  FaVenusMars,
} from "react-icons/fa";

import { CalculateAge } from "../../../utils/CalculateAge";
import TextInput from "../../../shared/TextInput";
import CustomButton from "../../../shared/CustomButton";
import DatePicker from "../../../shared/DatePicker";
import {
  useCreateSickLeaveMutation,
  useUpdateSickLeaveMutation,
} from "../../../redux/api/modules/beneficiaryApi";
import { extractTextFromHTML } from "../../../utils/ExtractTextFromHtml";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { getToFromDateRange } from "../../../utils/AmountToWords";
import CustomTable from "../../../shared/CustomTable";
import { FaFileMedical } from "react-icons/fa";
import CheckPermission from "../../../hooks/CheckPermission";

const CalculateDays = ({ isEdit }) => {
  const { values, setFieldValue } = useFormikContext();
  const { admit_date, discharge_date } = values;
  const isFirstRun = React.useRef(true);

  React.useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      if (isEdit) return;
    }

    if (admit_date && discharge_date) {
      const start = moment(admit_date);
      const end = moment(discharge_date);

      if (start.isValid() && end.isValid()) {
        const diff = end.diff(start, "days");
        const days = diff >= 0 ? diff + 1 : 0;
        if (days !== values.number_of_days) {
          setFieldValue("number_of_days", days);
        }
      }
    }
  }, [admit_date, discharge_date, setFieldValue, isEdit]);

  return null;
};

const CreateSickLeaveForm = ({
  emrData,
  patientData,
  setOpenCreateSickLeaveModal,
  setSelectEmrData,
  isEdit,
  leaveData,
  fetchInitialData,
}) => {
  const { hasPermission } = CheckPermission();
  const { t, i18n } = useTranslation();
  const auth = useSelector((state) => state.auth);
  const { roleData } = useSelector((state) => state?.auth);
  let diagnosisData = emrData?.diagnosisArr || [];
  let visitDate = emrData?.created_at
    ? new Date(emrData?.created_at).toISOString().split("T")[0]
    : "";

  const [chiefComplaint, setChiefComplaint] = useState(emrData?.medical_assessments?.chief_complaints?.value)
  const [createSickLeave, { isLoading: isCreating }] = useCreateSickLeaveMutation();
  const [updateSickLeave, { isLoading: isUpdating }] = useUpdateSickLeaveMutation();
  const isLoading = isCreating || isUpdating;

  const initialValues = {
    number_of_days: isEdit ? leaveData?.number_of_days : 1,
    visit_date: isEdit
      ? moment(leaveData?.visit_date).toDate()
      : moment(visitDate, "YYYY-MM-DD").toDate(),
    admit_date: isEdit
      ? leaveData?.admit_date
        ? moment(leaveData?.admit_date).toDate()
        : ""
      : "",
    discharge_date: isEdit
      ? leaveData?.discharge_date
        ? moment(leaveData?.discharge_date).toDate()
        : ""
      : "",
    occupation: isEdit ? leaveData?.occupation : patientData?.occupation ?? "",
    diagnosis: isEdit
      ? (typeof leaveData?.diagnosis === 'string' ? [] : leaveData?.diagnosis)
      : diagnosisData,
    remarks: isEdit ? leaveData?.remarks : "",
    recommendation: isEdit ? leaveData?.recommendation : "",
    significiant_signs: isEdit
      ? leaveData?.significiant_signs
      : extractTextFromHTML(chiefComplaint) ?? "",
    duration_of_illness: isEdit
      ? leaveData?.duration_of_illness
      : emrData?.vital_signs?.find(
        (v) => v.category === "duration-of-illness"
      )?.value || "",
  };
  const validationSchema = Yup.object().shape({
    visit_date: Yup.date().required(t("Visit date is required")),
    admit_date: Yup.date()
      .required(t("Sick leave start from is required"))
      .max(
        Yup.ref("discharge_date"),
        t("Sick leave start from must not be after To date")
      ),
    discharge_date: Yup.date()
      .required(t("To date is required"))
      .min(
        Yup.ref("admit_date"),
        t("To date must not be before Sick leave start from")
      ),
    // occupation: Yup.string().trim().required(t("Occupation is required")),
    diagnosis: Yup.array().min(1, t("At least one diagnosis is required")),
    significiant_signs: Yup.string().trim().required(t("Chief complaint is required")),
    recommendation: Yup.string().trim(),
    number_of_days: Yup.number()
      .required(t("Number of days is required"))
      .min(1, t("Number of days must be at least 1")),
  });

  const handleFormSubmit = async (values) => {
    if (!hasPermission("leave", isEdit ? "update" : "create")) return;
    const { startDate, endDate } = getToFromDateRange(values?.number_of_days);
    let payload = {
      ...values,
      emr_id: emrData?.emr_id,
      visit_date: moment(values?.visit_date).format("YYYY-MM-DD"),
      admit_date: moment(values?.admit_date).format("YYYY-MM-DD"),
      discharge_date: moment(values?.discharge_date).format("YYYY-MM-DD"),
      to: startDate,
      from: endDate,
      patient: patientData,
      practitioner: roleData,
    };
    if (isEdit) {
      payload.id = leaveData?.id;
      payload.status = 'Issued'
    }

    try {
      const response = isEdit
        ? await updateSickLeave(payload).unwrap()
        : await createSickLeave(payload).unwrap();
      if (response) {
        toast.success(response?.message);
        setOpenCreateSickLeaveModal(false);
        if (setSelectEmrData) {
          setSelectEmrData(null);
        }
        if (fetchInitialData) {
          fetchInitialData();
        }
      }
    } catch (error) {
      toast.error(
        error?.data?.message ||
          error?.message ||
          t("Failed to save sick leave")
      );
    }
  };

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 border p-3 rounded-lg bg-gray-100 mt-2">
        <div className="flex items-center">
          <FaUser className=" mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">
              {t("Patient Name")}:
            </strong>
            <span className="font-14-gray">{patientData?.full_name}</span>
          </p>
        </div>
        <div className="flex items-center">
          <FaUser className=" mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">{t("MRN")}:</strong>
            <span className="font-14-gray">{patientData?.mrn}</span>
          </p>
        </div>
        <div className="flex items-center">
          <FaVenusMars className="mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">
              {t("Gender")}:
            </strong>
            <span className="font-14-gray">{patientData?.gender}</span>
          </p>
        </div>
        <div className="flex items-center">
          <FaCalendar className=" mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">
              {t("Date of Birth")}:
            </strong>
            <span className="font-14-gray">{patientData?.dob}</span>
          </p>
        </div>
        <div className="flex items-center">
          <FaCalendar className="mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">{t("Age")}:</strong>
            <span className="font-14-gray">
              {CalculateAge(patientData?.dob)}
            </span>
          </p>
        </div>
        <div className="flex items-center">
          <FaBriefcaseMedical className="mr-2" />
          <p>
            <strong className="text-16-gray font-bold mr-2">
              {t("Medical Record No")}:
            </strong>
            <span className="font-14-gray">{emrData?.emr_id || "--"}</span>
          </p>
        </div>
      </div>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {(props) => (
          <Form>
            <CalculateDays isEdit={isEdit} />
            <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-4">
              <TextInput
                name="occupation"
                label={t("Occupation & Place of Works*")}
                placeholder={t("Enter occupation")}
              />
              <DatePicker
                name="visit_date"
                label={t("Date Of Visit*")}
                placeholder={t("Add visit date")}
              />
              <DatePicker
                name="admit_date"
                label={t("Sick leave start from*")}
                placeholder={t("Add start date")}
              />
              <DatePicker
                name="discharge_date"
                label={t("To*")}
                placeholder={t("Add end date")}
              />
              <TextInput
                name="number_of_days"
                type="number"
                step="any"
                label={t("Number of Days*")}
                placeholder={t("Add number of days")}
              />
              <TextInput
                name="duration_of_illness"
                label={t("Duration of Illness")}
                placeholder={t("Enter duration of illness")}
              />
              <div className="flex flex-col gap-1 col-span-2">
                <label className="input-label">
                  {t("Diagnosis*")}
                </label>
                <div className="border rounded-lg overflow-hidden">
                  <CustomTable
                    columns={[
                      { field: "codeId", header: t("Code") },
                      { field: "codeDescription", header: t("Description") },
                      { field: "type", header: t("Type") },
                      // { field: "on_admission", header: t("On Admission") },
                    ]}
                    data={props.values.diagnosis}
                  />
                </div>
                {props?.errors.diagnosis && props?.touched.diagnosis && (
                  <div className="text-red-500 text-sm mt-1">
                    {props?.errors.diagnosis}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label htmlFor="significiant_signs" className="input-label">
                  {t("Chief Complaint / Significant Signs*")}
                </label>
                <Field
                  as="textarea"
                  name="significiant_signs"
                  placeholder={t("Enter chief complaint")}
                  className="textarea-field"
                />
                {props?.errors.significiant_signs && props?.touched.significiant_signs && (
                  <div className="text-red-500 text-sm mt-1">
                    {props?.errors.significiant_signs}
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label htmlFor="recommendation" className="input-label">
                  {t("Recommendation")}
                </label>
                <Field
                  as="textarea"
                  name="recommendation"
                  placeholder={t("Enter recommendation")}
                  className="textarea-field"
                />
              </div>
              <div className="flex flex-col gap-1 col-span-2">
                <label htmlFor="remarks" className="input-label">
                  {t("Remarks")}
                </label>
                <Field
                  as="textarea"
                  name="remarks"
                  placeholder={t("Enter remarks")}
                  className="textarea-field"
                />
              </div>
            </div>
            <div className="flex items-center justify-end mt-3">
              {hasPermission("leave", isEdit ? "update" : "create") && <CustomButton
                loading={isLoading}
                type="submit"
                icon={<FaCheckDouble size={12} className="me-2" />}
                label={isEdit ? t("Update Sick Leave") : t("Create Sick Leave")}
                buttonType="primary"
              />}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CreateSickLeaveForm;
